package com.klantenstop.web.pages.search

import com.klantenstop.web.data.models.domain.SearchParams
import com.klantenstop.web.data.models.domain.toCompany
import com.klantenstop.web.data.models.domain.toCompanyPin
import com.klantenstop.web.data.models.domain.toCoordinates
import com.klantenstop.web.data.repos.CategoryRepo
import com.klantenstop.web.data.repos.CompanyRepo
import com.klantenstop.web.data.repos.CuratedCityRepo
import com.klantenstop.web.utils.AnalyticsManager
import com.klantenstop.web.utils.MVI
import com.klantenstop.web.utils.catchWithCancellation
import com.klantenstop.web.utils.toCitySlug
import com.materialkobweb.components.toast.ToastManager
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class SearchViewModel(
    private val categoryRepo: CategoryRepo = CategoryRepo,
    private val companyRepo: CompanyRepo = CompanyRepo,
    private val curatedCityRepo: CuratedCityRepo = CuratedCityRepo,
) : ViewModel(), MVI<SearchContract.State, SearchContract.Intent, SearchContract.Effect> {

    private val _state = MutableStateFlow(SearchContract.State())
    override val state: StateFlow<SearchContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<SearchContract.Effect>()
    override val effect: SharedFlow<SearchContract.Effect> = _effect.asSharedFlow()

    // Track in-flight search job to cancel it if a new search or clear is requested. Both the
    // paginated companies call and the unpaginated pins call are awaited and applied to state
    // together in one updateState -- see search() -- so a page-change re-search and a
    // filter-change re-search can never apply their results out of order with one another.
    private var searchJob: Job? = null

    init {
        sendIntent(SearchContract.Intent.LoadCategories)
        sendIntent(SearchContract.Intent.LoadCuratedCities)
    }

    override fun sendIntent(intent: SearchContract.Intent) = coroutineScope.launch {
        when (intent) {
            is SearchContract.Intent.LoadCategories -> loadCategories()
            is SearchContract.Intent.LoadCuratedCities -> loadCuratedCities()
            is SearchContract.Intent.ClearSearch -> {
                searchJob?.cancel()
                searchJob = null
                updateState {
                    it.copy(
                        companies = emptyList(),
                        searchParams = intent.searchParams,
                        total = 0,
                        page = 1,
                        pins = emptyList(),
                        mapCenter = null
                    )
                }
            }

            is SearchContract.Intent.Search -> {
                val refetchPins = intent.searchParams != _state.value.searchParams

                searchJob?.cancel()
                searchJob = launch {
                    search(intent.searchParams, intent.page, refetchPins)
                }
            }
        }
    }

    private suspend fun loadCategories() {
        val categories = catchWithCancellation(
            block = { categoryRepo.getCategories() },
            onError = { e ->
                ToastManager.error(
                    e.message ?: "Er is een fout opgetreden bij het laden van categorieën. Probeer het later opnieuw.",
                    title = "Laden mislukt"
                )
            }
        ) ?: emptyList()

        updateState { it.copy(categories = categories, loading = false) }
    }

    private suspend fun loadCuratedCities() {
        val curatedCities = catchWithCancellation(
            block = { curatedCityRepo.getCuratedCities() },
            onError = { e ->
                ToastManager.error(
                    e.message ?: "Er is een fout opgetreden bij het laden van locaties. Probeer het later opnieuw.",
                    title = "Laden mislukt"
                )
            }
        ) ?: emptyList()

        updateState { it.copy(curatedCities = curatedCities) }
    }

    private suspend fun search(
        searchParams: SearchParams,
        page: Int = 1,
        refetchPins: Boolean
    ) = withContext(Dispatchers.Default) {
        val citySlug = searchParams.toCitySlug()

        val companiesDeferred = async {
            catchWithCancellation(
                block = {
                    companyRepo.getCompanies(
                        categoryIdentifier = searchParams.category?.publicIdentifier,
                        citySlug = citySlug,
                        radiusInKm = searchParams.radius.range,
                        page = page
                    )
                },
                onError = { e ->
                    ToastManager.error(
                        e.message ?: "Er is een fout opgetreden bij het zoeken naar bedrijven. Probeer het later opnieuw.",
                        title = "Zoeken mislukt"
                    )
                }
            )
        }

        val pinsDeferred = if (refetchPins) {
            async {
                catchWithCancellation(
                    block = {
                        companyRepo.getCompanyPins(
                            categoryIdentifier = searchParams.category?.publicIdentifier,
                            citySlug = citySlug,
                            radiusInKm = searchParams.radius.range
                        )
                    },
                    onError = { e ->
                        ToastManager.error(
                            e.message ?: "Er is een fout opgetreden bij het laden van kaartmarkeringen. Probeer het later opnieuw.",
                            title = "Laden mislukt"
                        )
                    }
                )
            }
        } else null

        val result = companiesDeferred.await()
        val pinsResult = pinsDeferred?.await()

        val companies = result?.items?.map { it.toCompany() } ?: emptyList()
        val total = result?.total ?: 0
        val resolvedPage = result?.page ?: page
        val pageSize = result?.pageSize ?: _state.value.pageSize

        val previousPage = _state.value.page

        updateState {
            it.copy(
                companies = companies,
                searchParams = searchParams,
                total = total,
                page = resolvedPage,
                pageSize = pageSize,
                // A failed refetch must clear pins; only a skipped (page-only) search keeps them
                pins = if (refetchPins) {
                    pinsResult?.items?.mapNotNull { pin -> pin.toCompanyPin() } ?: emptyList()
                } else {
                    it.pins
                },
                mapCenter = if (refetchPins) pinsResult?.center?.toCoordinates() else it.mapCenter
            )
        }

        // A requested page beyond the last page that actually has results (stale bookmark/shared
        // link, or the result set shrank since) comes back with empty items even though the total
        // count is positive -- redirect to the last valid page instead of showing a false "no
        // results" empty state.
        val lastPage = if (total > 0) ((total + pageSize - 1) / pageSize).toInt() else 1
        if (companies.isEmpty() && total > 0 && resolvedPage > lastPage) {
            emitEffect(SearchContract.Effect.PageOutOfRange(correctedPage = lastPage))
            return@withContext
        }

        // Only a submitted filter search counts as a search; page navigation is tracked separately
        if (!refetchPins) {
            if (resolvedPage != previousPage) {
                // Track when a user navigates to a different page of results
                AnalyticsManager.trackEvent(
                    AnalyticsManager.Events.SEARCH_PAGE_CHANGED,
                    mapOf(
                        "page" to resolvedPage,
                        "previous_page" to previousPage,
                        "has_category_filter" to (searchParams.category != null),
                        "has_location_filter" to searchParams.query.isNotBlank(),
                        "result_count" to companies.size
                    )
                )
            }
        } else {
            AnalyticsManager.trackEvent(
                AnalyticsManager.Events.SEARCH_PERFORMED,
                mapOf(
                    "query_length" to searchParams.query.length,
                    "query_is_empty" to searchParams.query.isBlank(),
                    "has_category_filter" to (searchParams.category != null),
                    "has_location_filter" to searchParams.query.isNotBlank(),
                    "radius_km" to searchParams.radius.range,
                    "result_count" to companies.size
                )
            )
        }
    }

    override fun emitEffect(effect: SearchContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (SearchContract.State) -> SearchContract.State) {
        _state.update(block)
    }
}
